import React from "react";
import { View, StyleSheet } from "react-native";
import BaseController from "../../common/base/BaseController";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import NtText from "../../components/NtText";
import NtPlanCard from "../components/NtPlanCard";
import { showToast } from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";
import { fetchPlans, updateTenantPlan } from "../../api/plans";
import { provisionVoip } from "../../api/ntconnect";
import { getPlan, currentPlanId, isPaid } from "../data/plans";

/**
 * Plans page — shows the tenant's current plan and lets an admin switch to
 * another tier. Backed by the local plan shim for now; when the BFF plan API
 * lands, only src/api/plans.js changes.
 *
 * "Current plan" is read from / written to user.planId via UserContext — that
 * field will be populated by the BFF tenant payload once it exists.
 */
// Friendly message for a failed VoIP provisioning (POST /ntconnect/voip).
// 201 + 409 never reach here (treated as success by the caller).
function provisionErrorMessage(res) {
  switch (res?.status) {
    case 401:
      return "Your session expired. Please sign in again.";
    case 429:
      return "Too many requests right now. Please try again in a moment.";
    case 502:
      return "The VoIP service is temporarily unavailable. Please try again.";
    default:
      return res?.data?.error || "Please try again, or contact support.";
  }
}

class PlanController extends BaseController {
  state = {
    ...super.state,
    plans: [],
    loadingPlanId: null,
  };

  componentDidMount() {
    super.componentDidMount?.();
    document.title = "Voxcraft | Plans";
    this.loadPlans();
  }

  loadPlans = async () => {
    const res = await fetchPlans();
    Logger("Plans · loadPlans", res);
    if (res.ok) this.setState({ plans: res.data });
  };

  handleSelect = async (planId) => {
    const user = this.context?.user;
    if (currentPlanId(user) === planId) return;

    const target = getPlan(planId);
    this.setState({ loadingPlanId: planId });

    // Upgrading into a paid tier → the tenant needs a VoIP trunk. Provision it
    // BEFORE marking the plan changed. The call is idempotent: 201 (created)
    // and 409 (already provisioned) both mean "trunk ready". Any other failure
    // blocks the upgrade so we don't leave a paid tenant without a trunk.
    if (isPaid(target)) {
      const prov = await provisionVoip();
      Logger("Plans · provisionVoip", prov);
      const trunkReady = prov.ok || prov.status === 409;
      if (!trunkReady) {
        showToast(
          "Couldn't set up your VoIP",
          provisionErrorMessage(prov),
          "error",
        );
        this.setState({ loadingPlanId: null });
        return;
      }
    }

    const res = await updateTenantPlan(planId);
    Logger("Plans · updateTenantPlan", res);

    if (!res.ok) {
      showToast("Couldn't switch plan", res.error || "Please try again.", "error");
      this.setState({ loadingPlanId: null });
      return;
    }

    // Persist on the user object so the sidebar + this page reflect it now.
    this.context.updateUser({ ...user, planId: res.data.planId });

    const plan = getPlan(res.data.planId);
    showToast("Plan updated", `You're now on ${plan.name}.`, "success");
    this.setState({ loadingPlanId: null });
  };

  render() {
    const user = this.context?.user;
    const current = getPlan(currentPlanId(user));
    const { plans, loadingPlanId } = this.state;

    return (
      <NtRootPageContainer
        pageTitle="Plans"
        pageSubtitle={`You're on the ${current.name} plan. Upgrade any time — changes apply immediately.`}
        permission={this.handlePermissionCheck?.()}
      >
        <View style={styles.grid}>
          {plans.map((plan) => (
            <NtPlanCard
              key={plan.id}
              plan={plan}
              currentTier={current.tier}
              isCurrent={plan.id === current.id}
              loading={loadingPlanId === plan.id}
              onSelect={() => this.handleSelect(plan.id)}
            />
          ))}
        </View>

        <NtText style={styles.note}>
          Plans and pricing shown are placeholders while billing is being wired
          up — switching takes effect immediately but isn't charged yet.
        </NtText>
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    alignItems: "stretch",
  },
  note: {
    color: "#7a7d84",
    fontSize: 11.5,
    marginTop: 18,
    fontStyle: "italic",
  },
});

export default PlanController;
